import { FunctionComponent, memo } from 'react'
import useFooter from '@hooks/useFooter'
import FooterComponent from './FooterComponent'
import { getFooterItems } from './utils'
import { GetFooterItemsType } from './types'
import styled, { css } from 'styled-components'
import { mobileCSS, tabletCSS } from '@measures/responsive'
import useIsErrorPage from '@hooks/useIsErrorPage'

interface FooterWrapperProps {
  isError: boolean
}

const FooterWrapper = styled.footer<FooterWrapperProps>`
  ${({ isError }) =>
    isError &&
    css`
      /* 1594px is after talking to Ringier Advertising */
      @media screen and (min-width: 1594px) {
        display: grid;
        grid-template-columns: 994px;
        justify-content: center;
      }

      @media screen and (max-width: 1593px) {
        display: grid;
        grid-template-columns: 994px;
        justify-content: center;
      }

      ${tabletCSS(css`
        display: block;
      `)}

      ${mobileCSS(css`
        display: block;
      `)}
    `}
`

const Footer: FunctionComponent = () => {
  const { blick: footerBlick, ringier: footerRingier } = useFooter()
  const isErrorPage = useIsErrorPage()

  const {
    columns: columnsForMobileBlick,
    rows: rowsForMobileBlick,
    items: dataForMobileBlick,
  } = footerBlick
    ? getFooterItems(footerBlick.links, 2)
    : ({} as ReturnType<GetFooterItemsType>)

  const {
    columns: columnsForNonMobileBlick,
    rows: rowsForNonMobileBlick,
    items: dataForNonMobileBlick,
  } = footerBlick
    ? getFooterItems(footerBlick.links, 3)
    : ({} as ReturnType<GetFooterItemsType>)

  const {
    columns: columnsForMobileRingier,
    rows: rowsForMobileRingier,
    items: dataForMobileRingier,
  } = footerRingier
    ? getFooterItems(footerRingier.links, 2)
    : ({} as ReturnType<GetFooterItemsType>)

  const {
    columns: columnsForNonMobileRingier,
    rows: rowsForNonMobileRingier,
    items: dataForNonMobileRingier,
  } = footerRingier
    ? getFooterItems(footerRingier.links, 3)
    : ({} as ReturnType<GetFooterItemsType>)

  return (
    <FooterWrapper isError={!!isErrorPage}>
      {!!footerBlick && (
        <FooterComponent
          title={footerBlick.text}
          logoType="blick"
          blinkProps={footerBlick.link}
          columnsForMobile={columnsForMobileBlick}
          rowsForMobile={rowsForMobileBlick}
          dataForMobile={dataForMobileBlick}
          columnsForNonMobile={columnsForNonMobileBlick}
          rowsForNonMobile={rowsForNonMobileBlick}
          dataForNonMobile={dataForNonMobileBlick}
        />
      )}
      {!!footerRingier && (
        <FooterComponent
          isExpandable={true}
          initialExpandedState="collapsed"
          title={footerRingier.text}
          logoType="ringier"
          blinkProps={footerRingier.link}
          columnsForMobile={columnsForMobileRingier}
          rowsForMobile={rowsForMobileRingier}
          dataForMobile={dataForMobileRingier}
          columnsForNonMobile={columnsForNonMobileRingier}
          rowsForNonMobile={rowsForNonMobileRingier}
          dataForNonMobile={dataForNonMobileRingier}
        />
      )}
    </FooterWrapper>
  )
}

const MemoizedFooter = memo(Footer, () => true)

MemoizedFooter.displayName = 'MemoizedFooter'

export default MemoizedFooter
